import { useMemo } from 'react';

import { transformDeploymentStatus } from '../utils/deploymentUtils';

import { useTriggers } from '~/common/hooks/useTriggers';
import { findTriggerBySpec } from '~/common/utils/findTriggerBySpec';
import { TriggerCategory } from '~/common/utils/triggerCategoryIcons';
import { EnvironmentType } from '~/components/EnvironmentIndicator';
import { StatusKey } from '~/components/Table/cells/DeployStatusCell';
import { Deployment } from '~/entities/Deployment';
import { MetaData } from '~/entities/Metadata';

export type RunDeployment = {
  id: string;
  metadata?: MetaData;
  // Requested by the deployments query and read by the listing to flag a problem on the card
  // and on the table without opening the details sidesheet.
  errorCount?: string;
  oomCount?: string;
  lastError?: string;
  lastOMM?: string;
  deploymentStatus: {
    pipelineEngineBeta: string;
    pipelineEngineVersion: string;
  };
  activeConfiguration: {
    consumers: number;
    replicaCount: number;
    actualConsumers: number;
    minReplicaCount: number;
    environment: {
      name: EnvironmentType;
    };
  };
  availableReplicas: string;
  project: string;
  pipeline: {
    id: string;
    name: string;
    description: string;
    versionMajor: number;
    versionMinor: number;
    triggerCategory?: string;
    triggerSpec?: { name: string; type: string };
    trigger?: {
      iconURL?: string;
    };
    hasApiTrigger?: boolean;
    isUsingApiTrigger?: boolean;
    flowSpec?: unknown;
  };
  startTime: string;
  status: {
    text: string;
  };
};

export type DeploymentDataItem = {
  key: string;
  deployment: RunDeployment;
  pipelineData: {
    id: string;
    name: string;
    version: string;
    description: string;
    triggerCategory?: TriggerCategory;
    triggerSpec?: { name: string; type: string };
    trigger?: { iconURL?: string };
    hasApiTrigger?: boolean;
    isUsingApiTrigger?: boolean;
    flowSpec?: unknown;
  };
  statusData: {
    status: StatusKey;
    environment: EnvironmentType;
  };
  deployedByData: {
    deployedBy: string;
    deploymentTime: string;
    engineVersion: string;
  };
};

export const useDeploymentsData = (
  deployments: Deployment[],
  environment: EnvironmentType,
  activeRealm: string
): DeploymentDataItem[] => {
  const triggers = useTriggers(activeRealm);

  return useMemo(() => {
    if (!deployments?.length) return [];

    const transformedDeployments = deployments.map(transformDeploymentStatus);

    return transformedDeployments.map(deployment => {
      const matchingTrigger = findTriggerBySpec(
        triggers,
        deployment.pipeline!.triggerSpec
      );

      const enrichedDeployment = {
        ...deployment,
        pipeline: {
          ...deployment.pipeline,
          trigger: matchingTrigger
            ? {
                iconURL: matchingTrigger.iconURL
              }
            : undefined
        }
      };

      return {
        key: deployment.id,
        deployment: enrichedDeployment,
        pipelineData: {
          id: deployment.pipeline!.id,
          name: deployment.pipeline!.name,
          version: `${deployment.pipeline!.versionMajor}.${deployment.pipeline!.versionMinor}`,
          description: deployment.pipeline!.description || '',
          triggerCategory: deployment.pipeline!
            .triggerCategory as TriggerCategory,
          triggerSpec: deployment.pipeline!.triggerSpec,
          trigger: matchingTrigger
            ? { iconURL: matchingTrigger.iconURL }
            : undefined,
          // BACKEND-API-TRIGGER: drop when TriggerIcon no longer needs pipeline inference.
          hasApiTrigger: deployment.pipeline!.hasApiTrigger,
          isUsingApiTrigger: deployment.pipeline!.isUsingApiTrigger,
          flowSpec: deployment.pipeline!.flowSpec
        },
        statusData: {
          status: deployment?.status?.text.toLowerCase() as StatusKey,
          environment
        },
        deployedByData: {
          deployedBy: deployment.metadata?.userEmail || '',
          deploymentTime: deployment.startTime || '',
          engineVersion:
            deployment.deploymentStatus?.pipelineEngineVersion || ''
        }
      } as DeploymentDataItem;
    });
  }, [deployments, environment, triggers]);
};
